import React, { useEffect, useRef } from 'react';
import { GridEvents } from '@mui/x-data-grid/models/events';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';

/**
 * This function is a custom React hook that saves and restores the state of the ag-Grid React component
 * using the local storage. It takes a mutable ref to the Grid API and a key to identify the grid state.
 *
 * Listens to column-related changes on grid, and saves column settings to local storage.
 * Restores settings upon reload.
 *
 * Usage:
 * `
 * function MyGrid() {
 *   const apiRef = useGridApiRef()
 *   usePersistColumnSettings(apiRef, 'customers-grid')
 *   return <DataGrid apiRef={apiRef} />
 * }
 * `
 * Note: to persist column visibility, you may need to add the following to grid props:
 *  `initialState={{ columns: { columnVisibilityModel: {} } }}`
 *
 * @param {MutableRefObject<GridApiPremium>} apiRef - a ref to the Grid API of the ag-Grid React component
 * @param {string} key - a string that identifies the grid state
 * @param {any} savedGridState - the saved grid state - state that has been saved in local storage (fallback is defaultGridState)
 * @param {any} defaultGridState - the default grid state - default grid state for reset
 */
export function usePersistGridState(
  apiRef: React.MutableRefObject<GridApiPremium>,
  key: string,
  savedGridState: any,
  defaultGridState: any
) {
  const initialized = useRef(false);

  /**
   * Resets the layout to default values and saves the changes to local storage.
   *
   * @return {void} Nothing is returned from this function.
   */
  const resetLayout = (): void => {
    if (!apiRef.current) {
      return;
    }

    const gridState = defaultGridState;
    const newGridState = { ...gridState };
    newGridState.preferencePanel = { open: false };
    localStorage.setItem(key, JSON.stringify(newGridState));

    restoreGridState(apiRef.current, key, newGridState);
  };

  useEffect(() => {
    /* Reset initialized to false if the key changes */
    initialized.current = false;
  }, [key]);

  useEffect(() => {
    /* Main Functionality */
    const ref = apiRef.current;

    if (!ref?.subscribeEvent) return;

    // Restore state on first ref load
    if (!initialized.current) {
      initialized.current = true;

      restoreGridState(ref, key, savedGridState);
    }

    const subs: VoidFunction[] = [];

    /**
     * Saves the current state of the grid to local storage.
     *
     * @param None
     * @return None
     */
    const save = () => {
      const state = ref.exportState();

      if (state?.columns && (state?.columns?.orderedFields ?? []).includes('__check__')) {
        state.columns.orderedFields = state?.columns?.orderedFields?.filter(field => field !== '__check__');
      }

      if (state) {
        localStorage.setItem(key, JSON.stringify(state));
      }
    };

    /**
     * Subscribes to a GridEvent and adds the subscription to the subs array.
     *
     * @param {E} event - The GridEvent to subscribe to.
     */
    const subscribe = <E extends GridEvents>(event: E) => {
      subs.push(ref.subscribeEvent(event, save));
    };

    /* Subscribe to specified GridEvents */
    subscribe('columnResizeStop');
    subscribe('columnOrderChange');
    subscribe('pinnedColumnsChange');
    subscribe('columnVisibilityModelChange');
    subscribe('sortModelChange');
    subscribe('columnWidthChange');
    subscribe('filterModelChange');

    /* Unsubscribe from GridEvents */
    return () => {
      subs.forEach(unsub => {
        unsub();
      });
    };
  }, [apiRef, savedGridState, key]);

  return { resetLayout };
}

/**
 * Restores the state of a grid from local storage, if found.
 *
 * @param {GridApiPremium} ref - The Grid API to restore the state of.
 * @param {string} storageKey - The key to use when looking up the grid state in local storage.
 */
function restoreGridState(ref: GridApiPremium, storageKey: string, defaultState: any) {
  const raw = localStorage.getItem(storageKey);

  const parsed = parseOrNull(raw);

  if (parsed) {
    try {
      // console.info(`Restoring grid state for ${storageKey}`, parsed);
      ref.restoreState(parsed);
    } catch (e) {
      console.warn(`Failed to restore grid state`, e);
    }
  } else {
    try {
      ref.restoreState(defaultState);
    } catch (e) {
      console.warn(`Failed to restore grid state`, e);
    }
  }
}

/**
 * Parses the given input if it is a stringified JSON object
 * and returns the parsed object. If the input is not a string
 * or is not parseable, it returns null.
 *
 * @param {unknown} raw - The raw input to be parsed
 * @return {object | null} - The parsed object or null if parsing failed
 */
function parseOrNull(raw: unknown): object | null {
  if (!raw) return null;

  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch (e) {
      console.warn(`Failed to parse: ${raw.substring(0, 50)}`);

      return null;
    }
  }

  return null;
}
